import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

const CreateTicket = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [productsServices, setProductsServices] = useState([]);
  const [formData, setFormData] = useState({
    subject: "",
    message: "",
    department_id: "",
    urgency: "Medium",
    related_product_service_id: "",
    cc_emails: "",
  });
  const [attachments, setAttachments] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchDepartments();
    fetchProductsServices();
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await api.get("/tickets/departments");
      if (response.data?.success && response.data?.data) {
        setDepartments(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  const fetchProductsServices = async () => {
    try {
      const response = await api.get("/tickets/products-services");
      if (response.data?.success && response.data?.data) {
        setProductsServices(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching products/services:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const allowedTypes = ["png", "jpg", "jpeg", "pdf", "doc", "xls", "xlsx"];
    const validFiles = files.filter((file) => {
      const ext = file.name.split(".").pop().toLowerCase();
      return allowedTypes.includes(ext);
    });

    if (validFiles.length !== files.length) {
      alert("Some files were skipped. Only png, jpg, jpeg, pdf, doc, xls, xlsx are allowed.");
    }

    const sizeValidFiles = validFiles.filter((file) => {
      if (file.size > 10 * 1024 * 1024) {
        alert(`File ${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      return true;
    });

    setAttachments([...attachments, ...sizeValidFiles]);
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    if (!formData.department_id) {
      newErrors.department_id = "Department is required";
    }

    if (formData.cc_emails) {
      const emails = formData.cc_emails.split(",").map((e) => e.trim());
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = emails.filter((email) => email && !emailRegex.test(email));
      if (invalidEmails.length > 0) {
        newErrors.cc_emails = "Invalid email format";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    const formDataToSend = new FormData();
    formDataToSend.append("subject", formData.subject);
    formDataToSend.append("message", formData.message);
    formDataToSend.append("department_id", formData.department_id);
    formDataToSend.append("urgency", formData.urgency);

    if (formData.related_product_service_id) {
      formDataToSend.append("related_product_service_id", formData.related_product_service_id);
    }

    if (formData.cc_emails) {
      formDataToSend.append("cc_emails", formData.cc_emails);
    }

    attachments.forEach((file) => {
      formDataToSend.append("attachments", file);
    });

    try {
      const response = await api.post("/tickets/create", formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data?.success) {
        alert("Ticket created successfully!");
        navigate(`/superadmin/tickets/${response.data.data.id || response.data.data.ticket_id}`);
      } else {
        alert(response.data?.message || "Failed to create ticket");
      }
    } catch (error) {
      console.error("Error creating ticket:", error);
      alert("Failed to create ticket");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow p-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">Create New Support Ticket</h1>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Enter ticket subject"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    errors.subject ? "border-red-500" : "border-gray-300 focus:ring-blue-500"
                  }`}
                />
                {errors.subject && <p className="text-red-500 text-sm mt-1">{errors.subject}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="department_id"
                    value={formData.department_id}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                      errors.department_id ? "border-red-500" : "border-gray-300 focus:ring-blue-500"
                    }`}
                  >
                    <option value="">Select Department</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                  {errors.department_id && (
                    <p className="text-red-500 text-sm mt-1">{errors.department_id}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Urgency <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="urgency"
                    value={formData.urgency}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Related Product/Service (Optional)
                  </label>
                  <select
                    name="related_product_service_id"
                    value={formData.related_product_service_id}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">None</option>
                    {productsServices.map((ps) => (
                      <option key={ps.id} value={ps.id}>
                        {ps.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CC Emails (Optional)</label>
                  <input
                    type="text"
                    name="cc_emails"
                    value={formData.cc_emails}
                    onChange={handleChange}
                    placeholder="email1@example.com, email2@example.com"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                      errors.cc_emails ? "border-red-500" : "border-gray-300 focus:ring-blue-500"
                    }`}
                  />
                  <p className="text-xs text-gray-500 mt-1">Separate multiple emails with commas</p>
                  {errors.cc_emails && <p className="text-red-500 text-sm mt-1">{errors.cc_emails}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={6}
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Describe your issue in detail..."
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    errors.message ? "border-red-500" : "border-gray-300 focus:ring-blue-500"
                  }`}
                />
                {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Attachments (Optional)</label>
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  accept=".png,.jpg,.jpeg,.pdf,.doc,.xls,.xlsx"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Allowed: png, jpg, jpeg, pdf, doc, xls, xlsx (Max 10MB per file, up to 10 files)
                </p>

                {attachments.length > 0 && (
                  <div className="mt-2">
                    <strong>Selected Files:</strong>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      {attachments.map((file, index) => (
                        <li key={index} className="flex justify-between items-center">
                          <span>
                            {file.name} ({(file.size / 1024).toFixed(2)} KB)
                          </span>
                          <button
                            type="button"
                            onClick={() => removeAttachment(index)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-between mt-6">
              <button
                type="button"
                onClick={() => navigate("/superadmin/tickets")}
                disabled={loading}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-md font-semibold disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-md font-semibold disabled:opacity-50"
              >
                {loading ? "Creating..." : "Create Ticket"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateTicket;

